import { useEffect, useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { api } from '../../api/endpoints'
import { useI18n } from '../../i18n/I18nProvider'
import { Busy, ErrorBanner, Field, Notice, NumberInput, TextInput } from '../../ui/controls'
import type { AdminProduct, AdminSetting, ReportedPhoto } from '../../api/types'

/**
 * The operator console (安全架构 §21).
 *
 * Reaching any of it needs the role **and** a second factor answered this session. That is
 * enforced on the server; this layout only decides what to draw. A client-side check is a
 * courtesy to the operator, never a control.
 */
export function AdminLayout() {
  const { t } = useI18n()

  return (
    <div className="page stack">
      <h1>{t('admin.title')}</h1>

      <nav className="nav admin-nav">
        <NavLink to="/admin/reports">{t('admin.nav.reports')}</NavLink>
        <NavLink to="/admin/prices">{t('admin.nav.prices')}</NavLink>
        <NavLink to="/admin/settings">{t('admin.nav.settings')}</NavLink>
        <NavLink to="/admin/two-factor">{t('admin.nav.twofactor')}</NavLink>
      </nav>

      <p className="field-hint">{t('admin.audited')}</p>

      <Outlet />
    </div>
  )
}

export function AdminPrices() {
  const { t } = useI18n()

  const [products, setProducts] = useState<AdminProduct[] | null>(null)
  const [drafts, setDrafts] = useState<Record<string, number | null>>({})
  const [error, setError] = useState<unknown>(null)
  const [saved, setSaved] = useState(false)
  const [busy, setBusy] = useState(false)

  const load = () => api.admin.products().then(setProducts).catch(setError)

  useEffect(() => {
    void load()
  }, [])

  if (!products) {
    return error ? <ErrorBanner error={error} /> : <Busy />
  }

  return (
    <div className="stack">
      <h2>{t('admin.prices.title')}</h2>

      <ErrorBanner error={error} />
      {saved ? <Notice>{t('common.saved')}</Notice> : null}

      <Notice>{t('admin.prices.inFlight')}</Notice>

      {products.map((product) => (
        <section className="card stack" key={product.code}>
          <div className="kv">
            <span>
              {product.nameZh} / {product.nameEn}
            </span>
            <strong>
              {product.currency} {product.currentPrice.toFixed(2)}
            </strong>
          </div>

          <div className="range-row">
            <Field label={t('admin.prices.new')}>
              <NumberInput
                value={drafts[product.code] ?? null}
                onChange={(value) => {
                  setDrafts({ ...drafts, [product.code]: value })
                  setSaved(false)
                }}
                min={1}
              />
            </Field>

            <button
              type="button"
              className="primary"
              disabled={busy || drafts[product.code] == null}
              onClick={() => {
                const value = drafts[product.code]

                if (value == null) {
                  return
                }

                setError(null)
                setBusy(true)

                api.admin
                  .changePrice(product.code, value)
                  .then(() => {
                    setDrafts({ ...drafts, [product.code]: null })
                    setSaved(true)
                    return load()
                  })
                  .catch(setError)
                  .finally(() => setBusy(false))
              }}
            >
              {busy ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </section>
      ))}
    </div>
  )
}

export function AdminSettings() {
  const { t } = useI18n()

  const [settings, setSettings] = useState<AdminSetting[] | null>(null)
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [error, setError] = useState<unknown>(null)
  const [saved, setSaved] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = () =>
    api.admin
      .settings()
      .then((rows) => {
        setSettings(rows)
        setDrafts(Object.fromEntries(rows.map((r) => [r.key, r.value])))
      })
      .catch(setError)

  useEffect(() => {
    void load()
  }, [])

  if (!settings) {
    return error ? <ErrorBanner error={error} /> : <Busy />
  }

  return (
    <div className="stack">
      <h2>{t('admin.settings.title')}</h2>

      <ErrorBanner error={error} />
      <p className="field-hint">{t('admin.settings.onlySeeded')}</p>

      {settings.map((setting) => (
        <section className="card stack" key={setting.key}>
          <div className="kv">
            <code>{setting.key}</code>
            {saved === setting.key ? <span className="field-hint">{t('common.saved')}</span> : null}
          </div>

          {setting.description ? <p className="field-hint">{setting.description}</p> : null}

          <div className="range-row">
            <Field label={t('admin.settings.value')}>
              <TextInput
                value={drafts[setting.key] ?? ''}
                onChange={(value) => {
                  setDrafts({ ...drafts, [setting.key]: value })
                  setSaved(null)
                }}
              />
            </Field>

            <button
              type="button"
              className="primary"
              disabled={busy || drafts[setting.key] === setting.value}
              onClick={() => {
                setError(null)
                setBusy(true)

                api.admin
                  .updateSetting(setting.key, drafts[setting.key] ?? '')
                  .then(() => {
                    setSaved(setting.key)
                    return load()
                  })
                  .catch(setError)
                  .finally(() => setBusy(false))
              }}
            >
              {t('common.save')}
            </button>
          </div>
        </section>
      ))}
    </div>
  )
}

export function AdminReports() {
  const { t } = useI18n()

  const [rows, setRows] = useState<ReportedPhoto[] | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  const load = () => api.admin.photoReports().then(setRows).catch(setError)

  useEffect(() => {
    void load()
  }, [])

  if (!rows) {
    return error ? <ErrorBanner error={error} /> : <Busy />
  }

  const act = (run: Promise<unknown>) => {
    setError(null)
    setBusy(true)

    run
      .then(load)
      .catch(setError)
      .finally(() => setBusy(false))
  }

  return (
    <div className="stack">
      <h2>{t('admin.reports.title')}</h2>

      <ErrorBanner error={error} />
      <p className="field-hint">{t('admin.reports.rule')}</p>

      {rows.length === 0 ? (
        <Notice>{t('admin.reports.none')}</Notice>
      ) : (
        rows.map((row) => (
          <section className="card stack" key={row.photoId}>
            <div className="photo-row">
              {/*
                The operator is entitled to look at what was reported; that is the whole job.
                The serving endpoint decides, not this page.
              */}
              <img className="photo-preview" src={api.photo.url(row.photoId)} alt="" />

              <div className="stack">
                <div className="kv">
                  <span>{t('admin.reports.count')}</span>
                  <strong>{row.reportCount}</strong>
                </div>
                <div className="kv">
                  <span>{t('admin.reports.status')}</span>
                  <strong>{row.moderationStatus}</strong>
                </div>
                <span className="field-hint">
                  {t('admin.reports.first')} {new Date(row.firstReportedAt).toLocaleString()}
                </span>
                <span className="field-hint">
                  {t('admin.reports.last')} {new Date(row.lastReportedAt).toLocaleString()}
                </span>
              </div>
            </div>

            <p className="field-hint">{t('admin.reports.takedownMeaning')}</p>

            <div className="row-links">
              <button
                type="button"
                className="danger"
                disabled={busy}
                onClick={() => act(api.admin.photoTakedown(row.photoId))}
              >
                {t('admin.reports.takedown')}
              </button>

              <button
                type="button"
                className="link-button"
                disabled={busy}
                onClick={() => act(api.admin.photoRestore(row.photoId))}
              >
                {t('admin.reports.restore')}
              </button>

              <button
                type="button"
                className="link-button"
                disabled={busy}
                onClick={() => act(api.admin.photoNotify(row.photoId))}
              >
                {t('admin.reports.notify')}
              </button>
            </div>
          </section>
        ))
      )}
    </div>
  )
}
